import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { labelids } from "../../assets/data/TkExplanationProperty";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import TkCheckbox from "../../components/atoms/TkCheckbox";
import TkButton from "../../components/atoms/TkButton";
import { GlobalContext } from "../../context/globalContext";
import { AuthInfo, UserType } from "../../type/contextType/globalContextType";
import { httpLogin } from "./api/request";
import { formList } from "./parts";
import "./_style.scss";
import { IAPI_COM001_Response, IAPI_COM002_Response } from "../../type/apiType";
import { BusinessContext } from "../../context/businessContext";
import TkLabelTextForPassWord from "../../components/atoms/TkLabelTextForPassWord";
import TkLoginInfomation from "./components/components";

/**
 * SCRC010001 ログイン
 * @return SCRC010001
 */
const SCRC010001 = () => {
  const { control, handleSubmit } = useForm<any>({
    mode: "all",
    reValidateMode: "onChange",
  });

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const navigate = useNavigate();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accessUrlId = queryParams.get("access_id") ?? "";

  const [type, setType] = useState<"password" | "text">("password");

  useEffect(() => {
    setState({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // パスワードを表示する
  const handleCheck = (e: any) => {
    setType(e.target.checked ? "text" : "password");
  };

  /**
   * ログイン処理を実行する関数です。
   * @param data - ユーザーのログイン情報
   */
  const handleLogin = async (data: any) => {
    const authInfoMappers = {
      //　WAMログイン（機構）
      wam: (res: IAPI_COM002_Response): AuthInfo => ({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        userId: res.userId,
        name: res.name,
        dept: res.dept,
        division: res.mainDivision,
        email: res.email,
        confirmStatus: res.confirmStatus,
        fiscalYear: res.fiscalYear,
        userType: res.userType as UserType,
      }),
      // 法人ログイン
      web: (res: IAPI_COM001_Response): AuthInfo => ({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        userId: res.userId,
        contractorNo: res.contractorNo,
        name: res.name,
        email: res.email,
        userKbn: res.userKbn,
        fiscalYear: res.fiscalYear,
        confirmStatus: res.confirmStatus,
        businessOwnerClass: res.businessOwnerClass,
        contractApprovalDate: res.contractApprovalDate,
        contractDate: res.contractDate,
        userType: res.userType as UserType,
      }),
      // 社会福祉協議会
      sw: (res: any) => ({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        userId: res.userId,
        name: res.name,
        email: res.email,
        fiscalYear: res.fiscalYear,
        confirmStatus: res.confirmStatus,
        userType: res.userType as UserType,
      }),
    };

    handleResponse(
      {
        default: () =>
          httpLogin({ userId: data.id, password: data.password, accessUrlId }),
      }["default"](),
      authInfoMappers
    );
  };

  const handleResponse = async (apiCall: Promise<any>, authInfoMapper: any) => {
    try {
      const res: IAPI_COM001_Response = await apiCall;

      if (res.resultCd === "I0000") {
        let userType: "1" | "3" | "default" = res.userType as
          | "1"
          | "3"
          | "default";

        if (userType !== "1" && userType !== "3") {
          userType = "default";
        }

        const mapMaker = (res: any) => {
          const mapper: Record<string, () => any> = {
            default: () => authInfoMapper.web(res),
            "1": () => authInfoMapper.wam(res),
            "3": () => authInfoMapper.sw(res),
          };
          return mapper[userType]();
        };

        setState({
          ...state,
          authInfo: mapMaker(res),
          errorMsgId: "",
        });

        sessionStorage.setItem(
          "token",
          JSON.stringify({
            accessToken: res.accessToken,
            refreshToken: res.refreshToken,
          })
        );

        const movePage = {
          "1": "/SCRW110001",
          "3": "/SCRS080001",
          default: res.userKbn === "0" ? "/SCRC020001" : "/SCRD310001",
        }[userType];

        if (res.businessType) {
          setBusinessState({
            ...businessState,
            common: {
              ...businessState.common,
              businessType: res.businessType,
            },
          });
        }

        navigate(
          res.screenId ? `/${res.screenId}` : movePage,
          accessUrlId
            ? {
                state: {
                  applyId: res.applyId,
                  facilityNo: res.facilityNo,
                  staffNo: res.staffNo,
                  facilityName: res.facilityName,
                  cancelRequestFlg: res.cancelRequestFlg,
                },
              }
            : {}
        );
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box className="SCRC010001" data-testid="SCRC010001">
      <TkInfoManual>{labelids.labelid.LBLC010001_01}</TkInfoManual>

      <Box
        className="form"
        component="form"
        onSubmit={handleSubmit(handleLogin)}
      >
        {formList.map((item, index) => (
          <React.Fragment key={index}>
            <Controller
              name={item.name}
              control={control}
              rules={item.schema}
              render={({ field, formState: { errors } }) => (
                <TkLabelTextForPassWord
                  {...field}
                  className="text"
                  label={item.label}
                  type={item.name === "password" ? type : "text"}
                  inputProps={{ maxLength: item.maxLength }}
                  error={!!errors[item.name]}
                  helperText={errors[item.name]?.message as string}
                />
              )}
            />
          </React.Fragment>
        ))}

        <TkCheckbox label="パスワードを表示する" onClick={handleCheck} />

        <TkButton type="submit">利用規約に同意しログイン</TkButton>

        <Link to="/SCRC010002">ログインにお困りですか？</Link>
      </Box>

      {/** インシデント管理#415 暫定対応 */}
      {/* <TkLoginInfomation /> */}
    </Box>
  );
};

export default SCRC010001;
